'use client'

import { CircleStencil, Cropper, CropperRef } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'
import { BaseDialog } from '@dc-shared-web/components/base/BaseDialog'
import { Icon, iconColor } from '@dc-shared-web/components/base/Icon'
import { Button } from '@dc-shared-web/components/shadcn'
import { cn } from '@dc-shared-web/lib'
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faPenToSquare,
  faUpload,
  faSpinner,
  faTrash,
} from '@document-crunch/pro-regular-svg-icons'
import {
  ChangeEvent,
  FC,
  PropsWithChildren,
  ReactNode,
  useRef,
  useState,
} from 'react'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'

export type ImageStyle = 'rectangle' | 'circle'

export type UploadImageProps = {
  /** Whether an image is currently uploaded. This is useful for determining the overlay icon displayed on hover. */
  hasImage: boolean
  /** Callback function that indicates whether the image is valid after the user has selected it. */
  isSelectedImageValid: (file: File) => boolean
  /** Callback function to handle the image upload, receives base64 string of cropped image. */
  onImageUpload: (file: string) => Promise<void>
  /** Optional callback function to handle image removal. */
  onRemoveImage?: () => Promise<void>
  /** The size of the overlay icon (upload, edit and loading). */
  overlayIconSize?: SizeProp
  /** The style to use when cropping the image. */
  stencilShape?: ImageStyle
  /** React node to use as the upload trigger element. */
  trigger: ReactNode
  /** The style of overlay to show on hover. */
  triggerShape?: ImageStyle
}

export const UploadImage: FC<UploadImageProps> = ({
  hasImage,
  isSelectedImageValid,
  onImageUpload,
  onRemoveImage,
  overlayIconSize,
  stencilShape = 'rectangle',
  trigger,
  triggerShape = 'rectangle',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [file, setFile] = useState<string>('')
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isRemovingImage, setIsRemovingImage] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    /* We check we actually selected a file and that it is valid. The implementation of the isSelectedImageValid callback is up to the consumer of the component. */
    if (!file) return
    if (isSelectedImageValid(file)) return

    const fileUrl = URL.createObjectURL(file)

    setFile(fileUrl)
    setIsEditDialogOpen(true)
  }

  const triggerFileInput = () => {
    fileInputRef.current?.click()
  }

  const onEditDialogClose = () => {
    /* When we close the edit dialog, we need to clear the file input value so that the user can select the same image again if needed. */
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }

    setIsEditDialogOpen(false)
  }

  const onRemoveImageClick = () => {
    setIsConfirmDialogOpen(true)
  }

  const confirmImageRemove = async () => {
    setIsRemovingImage(true)
    await onRemoveImage?.()
    setIsRemovingImage(false)
    setIsConfirmDialogOpen(false)
  }

  const showRemoveButton = hasImage && !!onRemoveImage && !isRemovingImage
  const overlayIcon = isRemovingImage
    ? faSpinner
    : hasImage
      ? faPenToSquare
      : faUpload

  return (
    <>
      <div
        onClick={triggerFileInput}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className='tw-relative tw-cursor-pointer'
      >
        {/* This file input is visually hidden but needed for the file upload. */}
        <input
          ref={fileInputRef}
          type='file'
          accept='.png,.jpg,.jpeg'
          onChange={handleFileChange}
          className='tw-hidden'
        />
        {/* Consumers can pass in any React node to use as the visual upload trigger element. Have in mind that the component itself should do nothing since the functionallity is handled by the wrapper div. */}
        {trigger}
        {isHovering && (
          <Overlay
            triggerShape={triggerShape}
            showRemoveButton={showRemoveButton}
            onRemoveImageClick={onRemoveImageClick}
          >
            <Icon
              icon={overlayIcon}
              color={iconColor.white}
              size={overlayIconSize}
              className={cn(isRemovingImage && 'tw-animate-spin')}
            />
          </Overlay>
        )}
      </div>
      {/* The dialog has an isOpen prop but we also avoid rendering it when false so the internal state is reset. */}
      {isEditDialogOpen && (
        <EditImageDialog
          dialogTitle='Image Editor'
          isOpen={isEditDialogOpen}
          setIsOpen={onEditDialogClose}
          file={file}
          onImageUpload={onImageUpload}
          stencilShape={stencilShape}
        />
      )}
      <BaseDialog
        isOpen={isConfirmDialogOpen}
        onClose={() => {
          if (!isRemovingImage) {
            setIsConfirmDialogOpen(false)
          }
        }}
        size='small'
        title='Delete Image'
        primaryActionSlot={
          <Button
            onClick={confirmImageRemove}
            variant='destructive'
            disabled={isRemovingImage}
          >
            {isRemovingImage ? 'Deleting...' : 'Delete'}
          </Button>
        }
        secondaryActionSlot={
          <Button
            onClick={() => setIsConfirmDialogOpen(false)}
            variant='outline'
            disabled={isRemovingImage}
          >
            Cancel
          </Button>
        }
      >
        <p>Are you sure you want to delete the image?</p>
      </BaseDialog>
    </>
  )
}

const Overlay: FC<
  PropsWithChildren<Pick<UploadImageProps, 'triggerShape'>> & {
    showRemoveButton: boolean
    onRemoveImageClick: () => void
  }
> = ({ children, triggerShape, onRemoveImageClick, showRemoveButton }) => {
  return (
    <div
      className={cn(
        'tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-80 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-animate-in tw-fade-in-0',
        triggerShape === 'circle' && 'tw-rounded-full'
      )}
    >
      {showRemoveButton && (
        <div className='tw-absolute -tw-top-2 -tw-right-2 tw-z-50'>
          <Button
            onClick={(event) => {
              event.stopPropagation()

              onRemoveImageClick()
            }}
            variant='outline'
            shape='circle'
            size='sm'
            aria-label='Remove image'
          >
            <Icon icon={faTrash} color={iconColor.white} />
          </Button>
        </div>
      )}
      {children}
    </div>
  )
}

const ZOOM_INCREMENT = 1.25
const ZOOM_DECREMENT = 0.75

type EditImageDialogProps = Pick<
  UploadImageProps,
  'stencilShape' | 'onImageUpload'
> & {
  dialogTitle: string
  file: string
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const EditImageDialog: FC<EditImageDialogProps> = ({
  dialogTitle,
  file,
  isOpen,
  onImageUpload,
  setIsOpen,
  stencilShape,
}) => {
  const cropperRef = useRef<CropperRef>(null)
  const [isUploading, setIsUploading] = useState(false)

  const updateZoom = (offset: number) => {
    cropperRef.current?.zoomImage(offset)
  }

  const onSave = async () => {
    if (cropperRef.current) {
      setIsUploading(true)

      const image = cropperRef.current.getCanvas()?.toDataURL()
      if (!image) return

      await onImageUpload(image)

      setIsUploading(false)
      setIsOpen(false)
    }
  }

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={() => {
        if (!isUploading) {
          setIsOpen(false)
        }
      }}
      size='medium'
      title={dialogTitle}
      primaryActionSlot={
        <Button onClick={onSave} disabled={isUploading}>
          {isUploading ? 'Saving...' : 'Save'}
        </Button>
      }
      secondaryActionSlot={
        <Button
          onClick={() => {
            setIsOpen(false)
          }}
          variant='outline'
          disabled={isUploading}
        >
          Cancel
        </Button>
      }
    >
      <Cropper
        ref={cropperRef}
        src={file}
        stencilComponent={stencilShape === 'circle' ? CircleStencil : undefined}
        stencilProps={
          stencilShape === 'rectangle'
            ? {
                aspectRatio: 16 / 9,
              }
            : {
                aspectRatio: 1,
              }
        }
      />
      <div className='tw-flex tw-justify-center tw-gap-2 tw-mt-2'>
        <Button
          variant='ghost'
          onClick={() => updateZoom(ZOOM_INCREMENT)}
          disabled={isUploading}
        >
          <Icon icon={faMagnifyingGlassPlus} />
        </Button>
        <Button
          variant='ghost'
          onClick={() => updateZoom(ZOOM_DECREMENT)}
          disabled={isUploading}
        >
          <Icon icon={faMagnifyingGlassMinus} />
        </Button>
      </div>
    </BaseDialog>
  )
}
